import { useEffect, useMemo, useState } from "react";
import { Public } from "@/lib/api";
import { Link, navigate, useRoute } from "@/lib/router";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock } from "lucide-react";
import { Pagination } from "@/components/Pagination";
import { SkeletonList } from "@/components/ui/skeleton";
import { summarizeDynamicFields } from "@/lib/dynamic-fields";

export function CategoryListPage({ slug }: { slug: string }) {
  const route = useRoute();
  const [cat, setCat] = useState<any | null>(null);
  const [data, setData] = useState<{ items: any[]; page: number; total?: number } | null>(null);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [facets, setFacets] = useState<{
    bidders: Array<{ name: string; count: number }>;
    years: Array<{ year: number; count: number }>;
    provinces: Array<{ name: string; count: number }>;
  } | null>(null);
  const isSearch = !slug;

  useEffect(() => {
    if (!slug) { setCat(null); setFacets(null); return; }
    Public.categories().then((cs) => setCat(cs.find((c) => c.slug === slug) || null));
    Public.categoryFacets(slug).then(setFacets).catch(() => setFacets(null));
  }, [slug]);

  // URL → filters
  useEffect(() => {
    const f: Record<string, string> = {};
    Object.entries(route.query).forEach(([k, v]) => { if (k.startsWith("f.")) f[k.slice(2)] = v; });
    setFilters(f);
  }, [route.query]);

  const page = Number(route.query.page || 1);

  // 滚动到顶
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug, page, route.query.q]);

  useEffect(() => {
    setData(null);
    const q: Record<string, string | number> = { page, page_size: 20 };
    if (slug) q.category = slug;
    Object.entries(filters).forEach(([k, v]) => { if (v) q[`f.${k}`] = v; });
    if (route.query.q) q.q = route.query.q;
    Public.projects(q).then(setData).catch(() => setData({ items: [], page }));
  }, [slug, page, JSON.stringify(filters), route.query.q]);

  const schema: any[] = useMemo(() => Array.isArray(cat?.filter_schema) ? cat.filter_schema : [], [cat]);

  const apply = () => {
    const q: Record<string, string> = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) q[`f.${k}`] = v; });
    if (route.query.q) q.q = route.query.q;
    navigate(slug ? `/c/${slug}` : "/search", q);
  };
  const reset = () => {
    setFilters({});
    // 同时清除关键词；若 URL 已无任何参数，hashchange 不会触发，所以本地 state 已直接清空
    navigate(slug ? `/c/${slug}` : "/search", {});
  };

  return (
    <div className="container mx-auto px-4 py-6 md:py-8">
      <div className="text-sm text-muted-foreground mb-4 flex items-center flex-wrap gap-1">
        <Link to="/" className="hover:text-foreground">首页</Link>
        <span>/</span>
        <span className="text-foreground">{isSearch ? "全站检索" : (cat?.name || slug)}</span>
        {route.query.q && <>
          <span>/</span>
          <span>关键词：<span className="text-foreground">“{route.query.q}”</span></span>
        </>}
      </div>

      <div className="grid lg:grid-cols-[260px_1fr] gap-6">
        <aside className="space-y-4">
          <Card>
            <CardContent className="p-4 space-y-4">
              <div>
                <h2 className="font-semibold">{isSearch ? "全站检索" : (cat?.name || "栏目")}</h2>
                <p className="text-xs text-muted-foreground mt-1">
                  {cat?.description || (isSearch ? "在全部栏目中按关键词检索" : "")}
                </p>
              </div>
              {isSearch && (
                <div className="space-y-1.5">
                  <Label className="text-xs">关键词</Label>
                  <Input
                    key={route.query.q || "empty"}
                    defaultValue={route.query.q || ""}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        const v = (e.target as HTMLInputElement).value.trim();
                        navigate("/search", v ? { q: v } : {});
                      }
                    }}
                    placeholder="回车检索"
                  />
                </div>
              )}
              {!isSearch && schema.length === 0 && (
                <p className="text-xs text-muted-foreground">该栏目暂未配置筛选字段</p>
              )}
              {schema.map((f) => {
                // 优先用动态聚合：bidder/province/year 覆盖 schema 默认行为
                const dynBidders = f.key === "bidder" && facets && facets.bidders.length > 0;
                const dynProvinces = f.key === "province" && facets && facets.provinces.length > 0;
                const dynYears = f.key === "year" && facets && facets.years.length > 0;
                const useDyn = dynBidders || dynProvinces || dynYears;
                return (
                <div key={f.key} className="space-y-1.5">
                  <Label className="text-xs">{f.label || f.key}</Label>
                  {useDyn ? (
                    <select
                      className="w-full h-9 rounded-md border bg-transparent px-2 text-sm"
                      value={filters[f.key] || ""}
                      onChange={(e) => setFilters((s) => ({ ...s, [f.key]: e.target.value }))}
                    >
                      <option value="">全部</option>
                      {dynBidders && facets!.bidders.map((b) => (
                        <option key={b.name} value={b.name}>{b.name}（{b.count}）</option>
                      ))}
                      {dynProvinces && facets!.provinces.map((p) => (
                        <option key={p.name} value={p.name}>{p.name}（{p.count}）</option>
                      ))}
                      {dynYears && facets!.years.map((y) => (
                        <option key={y.year} value={String(y.year)}>{y.year}（{y.count}）</option>
                      ))}
                    </select>
                  ) : f.type === "enum" && Array.isArray(f.options) ? (
                    <select
                      className="w-full h-9 rounded-md border bg-transparent px-2 text-sm"
                      value={filters[f.key] || ""}
                      onChange={(e) => setFilters((s) => ({ ...s, [f.key]: e.target.value }))}
                    >
                      <option value="">全部</option>
                      {f.options.map((o: string) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <Input
                      placeholder={f.placeholder || ""}
                      value={filters[f.key] || ""}
                      onChange={(e) => setFilters((s) => ({ ...s, [f.key]: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === "Enter") apply(); }}
                    />
                  )}
                </div>
                );
              })}
              {/* 物料 / 产品关键字（通用，独立于 schema） */}
              <div className="space-y-1.5">
                <Label className="text-xs">物料 / 产品关键字</Label>
                <Input
                  placeholder="如：变压器、电缆"
                  value={filters["material"] || ""}
                  onChange={(e) => setFilters((s) => ({ ...s, material: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === "Enter") apply(); }}
                />
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={apply}>筛选</Button>
                <Button size="sm" variant="outline" onClick={reset}>重置</Button>
              </div>
            </CardContent>
          </Card>
        </aside>

        <section>
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm text-muted-foreground">
              {data ? <>共 <span className="text-foreground font-medium">{data.items.length}</span> 条结果{Object.keys(filters).filter(k => filters[k]).length > 0 ? "（已筛选）" : ""}</> : "加载中..."}
            </div>
            <div className="text-xs text-muted-foreground">第 {page} 页</div>
          </div>
          <Card>
            <CardContent className="p-0">
              {!data ? (
                <SkeletonList rows={8} />
              ) : (
              <ul className="divide-y">
                {(data?.items || []).map((p) => (
                  <li key={p.id} className="px-4 py-3.5 flex items-start gap-4 hover:bg-muted/40 transition-colors">
                    <div className="flex-1 min-w-0">
                      <Link to={`/p/${p.slug}`} className="font-medium text-sm hover:text-primary inline-flex items-start gap-2 leading-snug">
                        {p.locked && (
                          <Badge variant="outline" className="shrink-0 border-amber-500/50 text-amber-700 text-[10px] px-1.5 py-0 h-5 mt-0.5">
                            <Lock className="h-2.5 w-2.5 mr-0.5" />VIP
                          </Badge>
                        )}
                        <span className="line-clamp-2">{p.title}</span>
                      </Link>
                      <div className="flex flex-wrap gap-2 mt-1.5">
                        {summarizeDynamicFields(p.dynamic_fields).map((b) => (
                          <Badge key={b.key} variant="secondary" className="text-[10px] font-normal">
                            {b.label}{b.label ? "：" : ""}{b.value}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground shrink-0">
                      {p.published_at ? new Date(p.published_at).toLocaleDateString() : ""}
                    </span>
                  </li>
                ))}
                {data && data.items.length === 0 && (
                  <li className="py-16 text-center space-y-3">
                    <p className="text-sm text-muted-foreground">没有匹配的内容</p>
                    <Button size="sm" variant="outline" onClick={reset}>清除筛选</Button>
                  </li>
                )}
              </ul>
              )}
            </CardContent>
          </Card>
          {data && (data.total ?? 0) > 0 && (
            <div className="mt-6">
              <Pagination
                page={page}
                total={data.total ?? 0}
                pageSize={20}
                onChange={(p) => navigate(slug ? `/c/${slug}` : "/search", { ...route.query, page: p })}
              />
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
